import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { getUserFraudSignals, recheckFraudSignals } from "../../services/admin";
import { toast } from "react-toastify";
import "./fraudsignalbadge.css";

const USER_TYPE_LABELS = {
  client: "Client",
  medium: "Medium",
};

const profileHref = (userType, id) => (userType === "medium" ? `/profile-view/${id}` : `/view/${id}`);

const formatReasons = (user) => {
  const summary = user && user.fraud_signal_summary ? user.fraud_signal_summary : {};
  const summaryReasons = summary.reasons || [];
  const flagReasons = user && user.fraud_flag_reasons ? user.fraud_flag_reasons : [];
  return [...summaryReasons, ...flagReasons].filter(Boolean);
};

export default function FraudSignalBadge({ client, userType = "client" }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fraudDetails, setFraudDetails] = useState(null);
  const [error, setError] = useState("");
  const [rechecking, setRechecking] = useState(false);
  const summary = client && client.fraud_signal_summary ? client.fraud_signal_summary : {};
  const duplicateScore = Number(summary.duplicate_score || 0);
  const emailScore = summary.email_fraud_score;
  const phoneScore = summary.phone_fraud_score;
  const matchedUserIds = summary.matched_user_ids || [];
  const reasons = formatReasons(client);
  const isFlagged = Number(client && client.fraud_flagged ? client.fraud_flagged : 0) === 1;
  const hasSignal = Boolean(summary.has_signal || isFlagged || duplicateScore > 0);
  const typeLabel = USER_TYPE_LABELS[userType] || USER_TYPE_LABELS.client;

  if (!hasSignal) {
    return <span className="fraud-risk-empty">-</span>;
  }

  const label = isFlagged ? "Flagged" : duplicateScore > 0 ? `Risk ${duplicateScore}` : "Checked";
  const matchedUsers = fraudDetails && fraudDetails.matched_users ? fraudDetails.matched_users : [];
  const detailSignals = fraudDetails && fraudDetails.signals ? fraudDetails.signals : [];

  const loadDetails = () => {
    setLoading(true);
    setError("");
    return getUserFraudSignals(client.id)
      .then((response) => {
        if (response && response.data && response.data.status) {
          setFraudDetails(response.data.data || {});
        } else {
          setError(response && response.data && response.data.message ? response.data.message : "Unable to load duplicate accounts.");
        }
      })
      .catch(() => {
        setError("Unable to load duplicate accounts.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openDetails = (event) => {
    event.stopPropagation();

    setModalOpen(true);

    // Only fetch detailed duplicate accounts for flagged users
    if (!isFlagged || fraudDetails || loading) {
      return;
    }

    loadDetails();
  };

  const closeDetails = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setModalOpen(false);
  };

  const runRecheck = (event) => {
    event.stopPropagation();

    if (rechecking) {
      return;
    }

    setRechecking(true);
    recheckFraudSignals(client.id)
      .then((response) => {
        if (response && response.data && response.data.status) {
          toast.success("Fraud re-check queued. Refresh in a moment to see the updated result.");
        } else {
          toast.error(response && response.data && response.data.message ? response.data.message : "Unable to queue the re-check.");
        }
      })
      .catch(() => {
        toast.error("Unable to queue the re-check.");
      })
      .finally(() => {
        setRechecking(false);
      });
  };

  const badgeClass = isFlagged
    ? "fraud-risk-badge fraud-risk-badge-flagged fraud-risk-button"
    : "fraud-risk-badge fraud-risk-badge-checked fraud-risk-button";

  return (
    <>
      <Tooltip
        arrow
        placement="top"
        title={
          <div className="fraud-risk-tooltip">
            <div>Duplicate score: {duplicateScore}</div>
            <div>Email score: {emailScore !== null && emailScore !== undefined ? emailScore : "-"}</div>
            <div>Phone score: {phoneScore !== null && phoneScore !== undefined ? phoneScore : "-"}</div>
            <div>Matched accounts: {matchedUserIds.length ? matchedUserIds.join(", ") : "-"}</div>
            {isFlagged && <div>Click to review duplicate accounts.</div>}
            {reasons.length > 0 && (
              <ul>
                {reasons.map((reason, index) => (
                  <li key={`${reason}-${index}`}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
        }
      >
        <button type="button" className={badgeClass} onClick={openDetails}>
          {label}
        </button>
      </Tooltip>

      {modalOpen && (
        <div className="fraud-modal-backdrop" onClick={closeDetails}>
          <div className="fraud-modal" onClick={(event) => event.stopPropagation()}>
            <div className="fraud-modal-header">
              <div>
                <h4>Fraud Risk Details</h4>
                <p>
                  {typeLabel} #{client.id} {client.email ? `- ${client.email}` : ""}
                </p>
              </div>
              <div className="fraud-modal-header-actions">
                <button type="button" className="fraud-modal-recheck" onClick={runRecheck} disabled={rechecking}>
                  {rechecking ? "Queueing..." : "Re-check"}
                </button>
                <button type="button" className="fraud-modal-close" onClick={closeDetails}>
                  &times;
                </button>
              </div>
            </div>

            <div className="fraud-modal-summary">
              <div>
                <span>Duplicate Score</span>
                <strong>{duplicateScore}</strong>
              </div>
              <div>
                <span>Email Score</span>
                <strong>{emailScore !== null && emailScore !== undefined ? emailScore : "-"}</strong>
              </div>
              <div>
                <span>Phone Score</span>
                <strong>{phoneScore !== null && phoneScore !== undefined ? phoneScore : "-"}</strong>
              </div>
              {matchedUserIds.length > 0 && (
                <div>
                  <span>Matched Account IDs</span>
                  <strong>{matchedUserIds.join(", ")}</strong>
                </div>
              )}
            </div>

            {reasons.length > 0 && (
              <div className="fraud-modal-section">
                <h5>Flag Reasons</h5>
                <ul className="fraud-modal-reasons">
                  {reasons.map((reason, index) => (
                    <li key={`${reason}-${index}`}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {isFlagged && (
              <div className="fraud-modal-section">
                <h5>Matched Duplicate Accounts</h5>
                {loading && <div className="fraud-modal-message">Loading duplicate accounts...</div>}
                {!loading && error && <div className="fraud-modal-error">{error}</div>}
                {!loading && !error && matchedUsers.length === 0 && (
                  <div className="fraud-modal-message">
                    No matched account details found. Matched IDs: {matchedUserIds.length ? matchedUserIds.join(", ") : "-"}
                  </div>
                )}
                {!loading && !error && matchedUsers.length > 0 && (
                  <div className="fraud-modal-table-wrap">
                    <table className="fraud-modal-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Type</th>
                          <th>Name</th>
                          <th>Screen Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Created</th>
                          <th>Status</th>
                          <th>Flagged</th>
                          <th>Profile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchedUsers.map((user) => {
                          const matchedType = Number(user.usertype) === 2 ? "medium" : "client";
                          return (
                            <tr key={user.id}>
                              <td>{user.id}</td>
                              <td>{USER_TYPE_LABELS[matchedType]}</td>
                              <td>{[user.first_name, user.last_name].filter(Boolean).join(" ") || "-"}</td>
                              <td>{user.screen_name || "-"}</td>
                              <td>{user.email || "-"}</td>
                              <td>{user.phone || "-"}</td>
                              <td>{user.created_at || "-"}</td>
                              <td>{user.status !== null && user.status !== undefined ? user.status : "-"}</td>
                              <td>{Number(user.fraud_flagged || 0) === 1 ? "Yes" : "No"}</td>
                              <td>
                                <a href={profileHref(matchedType, user.id)} target="_blank" rel="noopener noreferrer">
                                  View
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {detailSignals.length > 0 && (
              <div className="fraud-modal-section">
                <h5>Signal Checks</h5>
                <div className="fraud-signal-list">
                  {detailSignals.map((signal) => (
                    <div className="fraud-signal-row" key={signal.id}>
                      <span>Signal #{signal.id}</span>
                      <span>Duplicate: {signal.duplicate_score}</span>
                      <span>Email: {signal.email_fraud_score !== null && signal.email_fraud_score !== undefined ? signal.email_fraud_score : "-"}</span>
                      <span>Phone: {signal.phone_fraud_score !== null && signal.phone_fraud_score !== undefined ? signal.phone_fraud_score : "-"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
